import { useCallback, useEffect, useRef, useState } from 'react';
import { TopHeader } from './TopHeader';
import { BottomNav } from './BottomNav';

interface AppShellProps {
  children: React.ReactNode;
  /** When true, the bottom nav is permanently hidden (e.g. on the upload page). */
  hideBottomNav?: boolean;
}

/**
 * Tracks scroll direction to coordinate header/bottom-nav visibility.
 *
 * Rules:
 *  - At the very top of the page → header visible, bottom nav hidden
 *  - Scrolling DOWN → header slides up, bottom nav slides in
 *  - Scrolling UP  → header slides back in, bottom nav slides out
 *
 * Uses a small dead-zone (5 px) to prevent flicker from micro-scrolls.
 */
function useNavVisibility() {
  // `scrollingDown` = true  → header hidden, bottom nav shown
  // `scrollingDown` = false → header visible, bottom nav hidden
  const [scrollingDown, setScrollingDown] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const DEAD_ZONE = 5; // px of movement to ignore (prevents flicker)

  const update = useCallback(() => {
    const currentY = window.scrollY;
    const delta = currentY - lastScrollY.current;

    if (currentY <= 30) {
      // At the very top — always show header, hide bottom nav
      setScrollingDown(false);
    } else if (delta > DEAD_ZONE) {
      // Scrolling down — hide header, show bottom nav
      setScrollingDown(true);
    } else if (delta < -DEAD_ZONE) {
      // Scrolling up — show header, hide bottom nav
      setScrollingDown(false);
    }
    // Within the dead zone → keep current state (no flicker)

    lastScrollY.current = currentY;
    ticking.current = false;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [update]);

  return { scrollingDown };
}

export function AppShell({ children, hideBottomNav = false }: AppShellProps) {
  const { scrollingDown } = useNavVisibility();

  return (
    <div className="min-h-screen overflow-x-clip">
      <TopHeader hidden={scrollingDown} />
      <main className={hideBottomNav ? 'pt-[56px] pb-6' : 'pt-[56px] pb-[80px]'}>
        <div className="max-w-[480px] mx-auto px-4">
          {children}
        </div>
      </main>
      {!hideBottomNav && <BottomNav hidden={!scrollingDown} />}
    </div>
  );
}
